import { MAX_PHOTO_BYTES, SUPPORT_PHOTO_MEDIA_TYPES } from '@cottonray/support-contract'

/**
 * 문의에 붙일 사진 한 장 — **브라우저 판**.
 *
 * 네이티브 화면에서는 `expo-image-picker` 가 `quality: 0.6` 으로 줄여 줬다. 브라우저의
 * `<input type="file">` 에는 그런 손잡이가 없어서, 요즘 폰 사진(8~12MB)을 그대로 올리면
 * 서버·nginx 상한에 그대로 걸린다. 그래서 **여기서 줄인다** — 고른 자리에서 캔버스로 다시
 * 그려 JPEG 로 인코딩한다.
 */
export interface PreparedPhoto {
  /** 목록에서 지울 때 쓰는 키. 파일 이름은 같은 것이 여러 장 올 수 있어 못 쓴다 */
  id: string
  blob: Blob
  name: string
  type: string
  byteSize: number
  /** 미리보기 — 화면에서 뺄 때 `URL.revokeObjectURL` 로 돌려줘야 한다 */
  previewUrl: string
}

export type PhotoRejection = 'type' | 'size'

/**
 * 다시 그릴 기준. 문의 사진은 **화면이 어떻게 보이는지** 알면 되는 것이라 원본 해상도가
 * 필요 없다. 긴 변 1600px 이면 요즘 폰 화면을 원본 픽셀 그대로 담고도 남는다.
 */
const MAX_EDGE = 1600

/** 이 크기 아래면 굳이 다시 인코딩하지 않는다 — 손대면 화질만 떨어진다 */
const REENCODE_OVER_BYTES = 1_500_000

const JPEG_QUALITY = 0.72

let counter = 0

/**
 * 고른 파일 한 장을 붙일 수 있는 모양으로 만든다.
 *
 * **거절 이유까지 돌려준다** — "안 붙었다"만 알려 주면 사용자는 무엇을 고쳐야 할지 모른다.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto | PhotoRejection> {
  const supported = (SUPPORT_PHOTO_MEDIA_TYPES as readonly string[]).includes(file.type)
  const needsResize = !supported || file.size > REENCODE_OVER_BYTES

  let blob: Blob = file
  let type = file.type
  let name = file.name

  if (needsResize) {
    const shrunk = await reencode(file)
    // 디코딩 자체가 안 되면 이미지가 아니거나 브라우저가 모르는 형식이다(HEIC 등).
    // 원본이 우리가 받는 형식이면 그대로 보내 보고, 아니면 여기서 거른다
    if (shrunk === undefined) {
      if (!supported) return 'type'
    } else {
      blob = shrunk
      type = 'image/jpeg'
      name = `${stripExtension(file.name)}.jpg`
    }
  }

  // 줄이고도 넘치면 그때는 사용자가 다른 사진을 골라야 한다
  if (blob.size > MAX_PHOTO_BYTES) return 'size'

  counter += 1
  return {
    id: `photo-${counter}`,
    blob,
    name: name === '' ? 'support.jpg' : name,
    type,
    byteSize: blob.size,
    previewUrl: URL.createObjectURL(blob),
  }
}

/**
 * 캔버스로 다시 그려 JPEG 로 인코딩한다. 못 하면 `undefined` — **던지지 않는다.**
 * 사진 한 장 때문에 문의 화면이 죽으면 방금 쓴 글이 통째로 날아간다.
 *
 * `imageOrientation: 'from-image'` 가 EXIF 회전을 반영한다. 없으면 세로로 찍은 사진이
 * 눕고, 그 상태로 저장돼 어드민에서도 누워 보인다.
 */
async function reencode(file: File): Promise<Blob | undefined> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))

    const context = canvas.getContext('2d')
    if (context === null) return undefined
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    return await new Promise<Blob | undefined>((resolve) => {
      canvas.toBlob((result) => resolve(result ?? undefined), 'image/jpeg', JPEG_QUALITY)
    })
  } catch {
    return undefined
  }
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot <= 0 ? name : name.slice(0, dot)
}
